import React from "react";
import { useTranslation } from "react-i18next";
import { Coffee, Heart } from "lucide-react";

/**
 * Feature cards config.
 * Each card gets an image header (object-cover) above the icon + title + text.
 * The Tradizione card uses the "Custodi della Tradizione" badge as its icon
 * (in place of the generic Award icon) to give it a distinctive brand feel.
 */
const FEATURES = [
  {
    key: "miscela",
    icon: Coffee,
    image: "/card-sacks.jpg",
    imgFocus: "center 60%",
  },
  {
    key: "tradizione",
    // Badge used both as top image AND as circular icon overlay
    image: "/badge-custodi.png",
    imgIsBadge: true, // renders differently: contained on a cream backdrop
    badgeIcon: "/badge-custodi.png",
    imgFocus: "center center",
  },
  {
    key: "passione",
    icon: Heart,
    image: "/card-beans.jpg",
    imgFocus: "center 45%",
  },
];

const BrandIntro = () => {
  const { t } = useTranslation();

  return (
    <section
      id="brand-intro"
      className="section-y"
      style={{ background: "#fbf5e9" }}
      data-testid="brand-intro"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
        <div className="divider-beans font-serif-display uppercase tracking-[0.3em] text-xs mb-5">
          {t("brandIntro.eyebrow")}
        </div>
        <h2
          className="font-serif-display font-bold leading-tight"
          style={{ color: "#2a160c", fontSize: "clamp(1.9rem, 3.4vw, 3rem)" }}
        >
          {t("brandIntro.title")}
        </h2>
        <p
          className="mt-6 max-w-3xl mx-auto text-base sm:text-lg leading-relaxed"
          style={{ color: "#3b2417" }}
        >
          {t("brandIntro.p1")}
        </p>
        <p
          className="mt-4 max-w-3xl mx-auto text-base sm:text-lg leading-relaxed"
          style={{ color: "#3b2417" }}
          dangerouslySetInnerHTML={{ __html: t("brandIntro.p2") }}
        />

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {FEATURES.map(({ key, icon: Icon, image, imgIsBadge, badgeIcon, imgFocus }) => (
            <article
              key={key}
              className="group relative overflow-hidden rounded-2xl flex flex-col transition duration-300 hover:-translate-y-1 hover:shadow-xl"
              style={{
                background: "#f5ead6",
                border: "1px solid rgba(91,54,33,0.12)",
                boxShadow: "0 20px 40px -25px rgba(42,22,12,0.35)",
              }}
              data-testid={`brand-card-${key}`}
            >
              {/* Top visual header — same height across cards for balance */}
              <div className="relative w-full h-52 sm:h-56 overflow-hidden">
                {imgIsBadge ? (
                  <div
                    className="absolute inset-0 flex items-center justify-center"
                    style={{
                      background:
                        "radial-gradient(ellipse at center, #fbf5e9 0%, #f5ead6 55%, #ecdcbd 100%)",
                    }}
                  >
                    <img
                      src={image}
                      alt={t(`brandIntro.features.${key}.title`)}
                      className="max-h-[85%] max-w-[80%] object-contain drop-shadow-md transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <>
                    <img
                      src={image}
                      alt={t(`brandIntro.features.${key}.title`)}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      style={{ objectPosition: imgFocus }}
                      loading="lazy"
                    />
                    {/* Subtle bottom gradient so the icon disc sits nicely on top of any image */}
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(42,22,12,0.35) 100%)",
                      }}
                    />
                  </>
                )}
              </div>

              {/* Icon disc — sits between the image and text (floats a bit above card body) */}
              <div className="relative flex justify-start px-7">
                <div
                  className="-mt-7 w-14 h-14 rounded-full flex items-center justify-center ring-4"
                  style={{
                    background: badgeIcon ? "#fbf5e9" : "#c5371f",
                    boxShadow: "0 10px 20px -8px rgba(42,22,12,0.4)",
                    // Use cream ring so the disc reads as detached from the image
                    // (matches page background color)
                    ["--tw-ring-color"]: "#f5ead6",
                    borderColor: "#f5ead6",
                  }}
                >
                  {badgeIcon ? (
                    <img
                      src={badgeIcon}
                      alt=""
                      aria-hidden="true"
                      className="w-11 h-11 object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <Icon size={22} color="#fff" />
                  )}
                </div>
              </div>

              {/* Text content — preserved intact */}
              <div className="px-7 pt-3 pb-7 text-left">
                <h3
                  className="font-serif-display text-xl font-semibold"
                  style={{ color: "#2a160c" }}
                >
                  {t(`brandIntro.features.${key}.title`)}
                </h3>
                <p
                  className="mt-2 text-sm leading-relaxed"
                  style={{ color: "#5b3621" }}
                >
                  {t(`brandIntro.features.${key}.text`)}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BrandIntro;
